import { useState, useEffect, useCallback } from 'react';
import { Construction, Leaf, Handshake, ArrowRight } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Progress } from '../components/ui/progress';
import { Link } from 'react-router-dom';
import axios from 'axios';
import PageSEO from '../components/PageSEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

const statusLabels = {
  planifie: { label: 'Planifié', color: 'bg-blue-100 text-blue-700' },
  en_cours: { label: 'En cours', color: 'bg-gold-400/20 text-gold-700' },
  termine: { label: 'Terminé', color: 'bg-emerald-100 text-emerald-700' },
};

export const ProjetsPage = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchProjects = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/projects`);
      setProjects(response.data);
    } catch (error) {
      console.error('Error fetching projects:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  const infrastructureProjects = projects.filter(p => p.category === 'infrastructure');
  const environmentProjects = projects.filter(p => p.category === 'environment');
  const partnershipProjects = projects.filter(p => p.category === 'partnership');

  const getImgUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('/api/uploads/') || url.startsWith('/api/files/')) return `${BASE_URL}${url}`;
    return url;
  };

  const ProjectCard = ({ project }) => (
    <Link to={`/projets/${project.id}`} className="block" data-testid={`project-card-${project.id}`}>
    <Card className="card-teranga h-full hover:shadow-lg transition-shadow cursor-pointer">
      <CardContent className="p-6">
        {project.image_url && (
          <img 
            src={getImgUrl(project.image_url)} 
            alt={project.title || 'Projet de Gandiaye'}
            loading="lazy"
            className="w-full rounded-lg mb-4"
            style={{ height: 'auto', maxHeight: '240px', objectFit: 'contain', background: '#f5f7fa' }}
          />
        )}
        <div className="flex items-start justify-between gap-4 mb-4">
          <h3 className="font-heading font-semibold text-lg">{project.title}</h3>
          <span className={`text-xs font-semibold px-3 py-1 rounded-full whitespace-nowrap ${statusLabels[project.status]?.color || 'bg-neutral-100'}`}>
            {statusLabels[project.status]?.label || project.status}
          </span>
        </div>
        <p className="text-neutral-600 text-sm mb-4 line-clamp-3">
          {project.description}
        </p>
        {project.progress > 0 && (
          <div className="mb-4">
            <div className="flex justify-between text-sm mb-2">
              <span className="text-neutral-500">Avancement</span>
              <span className="font-semibold text-emerald-600">{project.progress}%</span>
            </div>
            <Progress value={project.progress} className="h-2" />
          </div>
        )}
        {project.budget && (
          <div className="text-sm text-neutral-500">
            <span className="font-medium">Budget:</span> {project.budget}
          </div>
        )}
      </CardContent>
    </Card>
    </Link>
  );

  const EmptyState = ({ message }) => (
    <div className="text-center py-12 bg-neutral-50 rounded-xl">
      <Construction className="w-16 h-16 text-neutral-300 mx-auto mb-4" />
      <p className="text-neutral-500">{message}</p>
      <p className="text-sm text-neutral-400 mt-2">Les projets seront bientôt ajoutés.</p>
    </div>
  );

  return (
    <main className="pt-14 md:pt-16" data-testid="projets-page">
      <PageSEO
        title="Projets & Développement"
        description="Suivez les projets de développement de la commune de Gandiaye : infrastructures, environnement et partenariats."
        path="/projets"
      />
      {/* Hero */}
      <section className="bg-emerald-700 py-16 md:py-24">
        <div className="container-main">
          <h1 className="text-4xl md:text-5xl font-heading font-bold text-white mb-4">
            Projets & Développement
          </h1>
          <p className="text-white/80 text-lg max-w-2xl">
            Suivez l'avancement des projets de développement de notre commune et découvrez nos partenariats.
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="py-12 bg-white">
        <div className="container-main">
          <Tabs defaultValue="infrastructure" className="w-full">
            <TabsList className="w-full flex flex-wrap justify-start gap-2 bg-neutral-100 p-2 rounded-xl mb-8">
              <TabsTrigger value="infrastructure" className="flex-1 md:flex-none" data-testid="tab-infrastructure">
                <Construction className="w-4 h-4 mr-2" />
                Infrastructures
              </TabsTrigger>
              <TabsTrigger value="environment" className="flex-1 md:flex-none" data-testid="tab-environment">
                <Leaf className="w-4 h-4 mr-2" />
                Environnement
              </TabsTrigger>
              <TabsTrigger value="partnership" className="flex-1 md:flex-none" data-testid="tab-partnership">
                <Handshake className="w-4 h-4 mr-2" />
                Partenariats
              </TabsTrigger>
            </TabsList>

            {/* Infrastructures */}
            <TabsContent value="infrastructure">
              <div>
                <h2 className="text-2xl font-heading font-bold text-neutral-800 mb-6">
                  Grands Travaux
                </h2>
                <p className="text-neutral-600 mb-8">
                  Routes, éclairage public, assainissement... Suivez les chantiers qui transforment Gandiaye.
                </p>

                {loading ? (
                  <div className="flex justify-center py-12">
                    <div className="loader"></div>
                  </div>
                ) : infrastructureProjects.length > 0 ? (
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {infrastructureProjects.map((project) => (
                      <ProjectCard key={project.id} project={project} />
                    ))}
                  </div>
                ) : (
                  <EmptyState message="Aucun projet d'infrastructure en cours." />
                )}
              </div>
            </TabsContent>

            {/* Environnement */}
            <TabsContent value="environment">
              <div>
                <h2 className="text-2xl font-heading font-bold text-neutral-800 mb-6">
                  Initiatives Environnementales
                </h2>
                <p className="text-neutral-600 mb-8">
                  Reboisement, gestion des déchets, protection des ressources naturelles.
                </p>

                {loading ? (
                  <div className="flex justify-center py-12">
                    <div className="loader"></div>
                  </div>
                ) : environmentProjects.length > 0 ? (
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {environmentProjects.map((project) => (
                      <ProjectCard key={project.id} project={project} />
                    ))}
                  </div>
                ) : (
                  <EmptyState message="Aucun projet environnemental en cours." />
                )}

                <Card className="mt-8 bg-emerald-50 border-none">
                  <CardHeader>
                    <CardTitle className="text-emerald-700">Set-Setal : Nettoyage Collectif</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-neutral-600">
                      Participez aux journées "Set-Setal" organisées régulièrement pour maintenir 
                      la propreté de notre commune. Consultez l'agenda pour les prochaines dates.
                    </p>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* Partenariats */}
            <TabsContent value="partnership">
              <div>
                <h2 className="text-2xl font-heading font-bold text-neutral-800 mb-6">
                  Coopération et Partenariats
                </h2>
                <p className="text-neutral-600 mb-8">
                  Découvrez nos partenaires au développement et les projets menés en coopération.
                </p>

                {loading ? (
                  <div className="flex justify-center py-12">
                    <div className="loader"></div>
                  </div>
                ) : partnershipProjects.length > 0 ? (
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {partnershipProjects.map((project) => (
                      <ProjectCard key={project.id} project={project} />
                    ))}
                  </div>
                ) : (
                  <EmptyState message="Aucun partenariat affiché pour le moment." />
                )}

                <Card className="mt-8 bg-gold-400/10 border-none">
                  <CardHeader>
                    <CardTitle className="text-gold-700 flex items-center gap-2">
                      <Handshake />
                      Appel à la Diaspora
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-neutral-600 mb-4">
                      La diaspora de Gandiaye est un partenaire clé du développement local. 
                      Vous souhaitez contribuer à un projet ? Proposez votre idée ou parrainez une initiative.
                    </p>
                    <a 
                      href="/diaspora" 
                      className="inline-flex items-center gap-2 text-emerald-600 font-semibold hover:gap-4 transition-all"
                    >
                      Découvrir l'Espace Diaspora <ArrowRight size={18} />
                    </a>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </section>
    </main>
  );
};

export default ProjetsPage;
